import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import { findIndex } from 'lodash';
import { LocationCardView } from '../LocationCard';
import { breakpoint } from '@zocdoc/components/src/grid';
import { Arrow, InvertedCheck, Question, Alert } from '@zocdoc/components/src/icons';
import { coral, green, grey10, grey75, purple, white } from '@zocdoc/branding/colors';
import { LOCALIZED_STRINGS, AnimationDelay } from './constants';
import { FormattedMessage } from 'react-intl';
import BreadcrumbDots from '@zocdoc/components/src/BreadcrumbDots';
import { FontFamily } from '@zocdoc/components/src/typography';
import SwipeHandler from '@zocdoc/components/src/SwipeHandler';
import { tween } from 'shifty';
import styled from 'styled-components';

export default class LocationCardCarouselView extends PureComponent {
    static propTypes = {
        animationNextLocationId: PropTypes.string,
        insuranceIsAccepted: PropTypes.bool,
        insuranceIsSelected: PropTypes.bool,
        locationId: PropTypes.string,
        locations: PropTypes.array.isRequired,
        onClickExpandMap: PropTypes.func,
        onClickNext: PropTypes.func.isRequired,
        onClickPrev: PropTypes.func.isRequired,
        showMapThumbnail: PropTypes.bool.isRequired,
    };

    animationViewportRef = React.createRef()
    locationCardRef = React.createRef()

    render() {
        return (
            <div>
                {this.renderCarousel()}
            </div>
        );
    }

    computeCarouselCardOffset(locationIndex) {
        return locationIndex * findDOMNode(this.locationCardRef.current).clientWidth;
    }

    componentDidMount() {
        const { locations, locationId } = this.props;
        const updatedLocationIndex = findIndex(locations, loc => loc.id === locationId);
        if (this.animationViewportRef.current && locations.length > 0) {
            const nextOffset = this.computeCarouselCardOffset(updatedLocationIndex);
            findDOMNode(this.animationViewportRef.current).scrollLeft = nextOffset;
        }
    }

    componentDidUpdate(prevProps) {
        const {
            locations, locationId, animationNextLocationId, callbackAfterAnimationCompletes,
        } = this.props;
        if (animationNextLocationId !== prevProps.animationNextLocationId) {
            const locationIndex = findIndex(locations, loc => loc.id === animationNextLocationId);
            const prevLocationIndex = findIndex(locations, loc => loc.id === prevProps.animationNextLocationId);
            if (locationIndex !== prevLocationIndex) {
                if (this.animationViewportRef.current && locations.length > 0) {
                    tween({
                        from: {
                            x: this.computeCarouselCardOffset(prevLocationIndex),
                        },
                        to: {
                            x: this.computeCarouselCardOffset(locationIndex),
                        },
                        duration: AnimationDelay,
                        step: ({ x }) => {
                            findDOMNode(this.animationViewportRef.current).scrollLeft = x;
                        },
                    })
                        .then(() => {
                            // For whatever reason, if you just call this function here, it will execute before the
                            // animation is fully complete, and stutter the animation due to heavy JS execution.
                            // So we need to kick ourselves to the back of the execution stack.
                            setTimeout(callbackAfterAnimationCompletes, 0);
                        });
                }
            }
        } else {
            // If there was no update to the animationNextLocationId let's just make sure the card is in the location
            // we expect it to be.  Ex: we might be first mounting the component and the index is already set above 0.
            const updatedLocationIndex = findIndex(locations, loc => loc.id === locationId);
            if (this.animationViewportRef.current && locations.length > 0) {
                const nextOffset = this.computeCarouselCardOffset(updatedLocationIndex);
                findDOMNode(this.animationViewportRef.current).scrollLeft = nextOffset;
            }
        }
    }

    maybeRenderInsuranceAcceptanceBanner() {
        const {
            insuranceIsAccepted,
            insuranceIsSelected,
            locations,
            showMapThumbnail,
        } = this.props;

        let icon;
        let message;

        if (!insuranceIsSelected) {
            return;
        } else if (typeof insuranceIsAccepted === 'undefined') {
            icon = (
                <Question
                    color={purple}
                    data-test="insurance-accepted-icon-question"
                />
            );
            message = LOCALIZED_STRINGS.CheckingInsurance;
        } else if (insuranceIsAccepted) {
            icon = (
                <InvertedCheck
                    color={green}
                    data-test="insurance-accepted-icon-check"
                />
            );
            message = LOCALIZED_STRINGS.InsuranceAccepted;
        } else {
            icon = (
                <Alert
                    color={coral}
                    data-test="insurance-accepted-icon-alert"
                />
            );
            message = LOCALIZED_STRINGS.InsuranceNotAccepted;
        }

        return (
            <InsuranceCheckBanner showMapThumbnail={showMapThumbnail}>
                <InsuranceCheckBannerText
                    data-test="insurance-check-banner-text"
                    multipleLocations={locations.length > 1}
                    showMapThumbnail={showMapThumbnail}
                >
                    <InsuranceIconContainer>{icon}</InsuranceIconContainer>
                    <FormattedMessage {...message} />
                </InsuranceCheckBannerText>
            </InsuranceCheckBanner>
        );
    }

    maybeRenderCarouselFooter = () => {
        const { locations, locationId } = this.props;

        const locationIndex = findIndex(locations, loc => loc.id === locationId);

        if (locations.length > 1) {
            return (
                <CarouselFooter>
                    <LocationNumberText
                        data-test="location-number-text"
                    >
                        <FormattedMessage
                            {...LOCALIZED_STRINGS.LocationCarouselIndex}
                            values={{
                                index: locationIndex + 1,
                                total: locations.length,
                            }}
                        />
                    </LocationNumberText>
                    <StyledBreadcrumbDots
                        activeDotIndex={locationIndex}
                        data-test="location-breadcrumb-dots"
                        numberOfDots={locations.length}
                    />
                </CarouselFooter>
            );
        }
    };

    renderArrow(dataTest, direction, callback) {
        if (this.props.locations.length > 1) {
            return (
                <Gutter
                    data-test={dataTest}
                    onClick={callback}
                >
                    <StyledArrow direction={direction} />
                </Gutter>
            );
        }
    }

    renderCarousel = () => {
        const {
            locations,
            locationId,
            animationNextLocationId,
            onClickExpandMap,
            showMapThumbnail,
            onClickPrev,
            onClickNext,
        } = this.props;

        const selectedLocation = animationNextLocationId || locationId;

        if (locations.length > 0) {
            const locationIndex = findIndex(locations, loc => loc.id === selectedLocation);

            if (locationIndex > -1) {
                // const displayedLocation = locations[locationIndex];
                return (
                    <CarouselOuterContainer data-test="location-card-carousel-desktop">
                        <CarouselInnerContainer data-test="location-card-carousel">
                            <SwipeHandler
                                direction={6}
                                onSwipeLeft={onClickNext}
                                onSwipeRight={onClickPrev}
                            >
                                {this.maybeRenderInsuranceAcceptanceBanner()}
                                <CarouselBody>
                                    {this.renderArrow('prev-arrow', 'left', onClickPrev)}
                                    <AnimationViewport
                                        ref={this.animationViewportRef}
                                    >
                                        <AnimationRail>
                                            <LeftSpacer />
                                            {locations.map(loc => (
                                                <CarouselItem
                                                    key={loc.id}
                                                    ref={this.locationCardRef}
                                                >
                                                    <LocationCardContainer
                                                        multipleLocations={locations.length > 1}
                                                        showMapThumbnail={showMapThumbnail}
                                                    >
                                                        <LocationCardView
                                                            {...loc}
                                                            onClickExpandMap={onClickExpandMap}
                                                            showMapThumbnail={showMapThumbnail}
                                                        />
                                                    </LocationCardContainer>
                                                </CarouselItem>
                                            ))}
                                            <RightSpacer />
                                        </AnimationRail>
                                    </AnimationViewport>
                                    {this.renderArrow('next-arrow', 'right', onClickNext)}
                                </CarouselBody>
                                {this.maybeRenderCarouselFooter(locations, locationIndex, showMapThumbnail)}
                            </SwipeHandler>
                        </CarouselInnerContainer>
                    </CarouselOuterContainer>
                );
            }
        }
    }
}

const CarouselOuterContainer = styled.div`
    position: relative;
    width: calc(100% + 32px);
    left: -16px;

    ${breakpoint.small} {
        left: 0;
        width: 100%;
    }
`;

const CarouselInnerContainer = styled.div`
    width: 100%;
    position: relative;

    ${breakpoint.small} {
        background: ${white};
    }
`;

const AnimationViewport = styled.div`
    width: 100%;
    overflow: hidden;
    position: relative;
`;

const AnimationRail = styled.div`
    overflow: visible;
    display: flex;
    flex-direction: row;
`;

const LeftSpacer = styled.div`
    min-width: 12px;

    ${breakpoint.small} {
        display: none;
    }
`;

const RightSpacer = styled.div`
    min-width: 16px;

    ${breakpoint.small} {
        display: none;
    }
`;

const CarouselItem = styled.div`
    position: relative;
    min-width: calc(100% - 28px);
    padding-left: 4px;

    ${breakpoint.small} {
        min-width: 100%;
        padding-left: 0;
    }
`;

const CAROUSEL_FOOTER_HEIGHT = 32;
const getMinHeight = p => p.multipleLocations ? CAROUSEL_FOOTER_HEIGHT : 0;

const LocationCardContainer = styled.div`
    vertical-align: middle;
    background-color: ${white};
    min-height: ${p => p.showMapThumbnail ? 64 : 0}px;
    padding: 15px 16px ${p => p.multipleLocations ? CAROUSEL_FOOTER_HEIGHT : 15}px;
    height: 100%;

    ${breakpoint.small} {
        width: 100%;
        min-height: ${p => p.showMapThumbnail ? 120 + getMinHeight(p) : getMinHeight(p)}px;
    }
`;

const INSURANCE_BANNER_HEIGHT = 40;

const InsuranceCheckBanner = styled.div`
    align-items: center;
    background-color: ${white};
    border-bottom: 1px solid ${grey10};
    display: flex;
    height: ${INSURANCE_BANNER_HEIGHT}px;
    margin: 0 16px;
    padding: 0 16px;
    position: relative;
    width: calc(100% - 32px);
    z-index: 1;

    ${breakpoint.small} {
        height: 44px;
        width: 100%;
        padding: 0;
        margin: 0 0 1px;
    }
`;

const InsuranceCheckBannerText = styled.span`
    font: 13px/24px ${FontFamily.SharpSansSemibold};

    ${breakpoint.small} {
        font-size: 15px;
        margin-left: ${p => p.multipleLocations ? 36 : 16}px;
    }
`;

const InsuranceIconContainer = styled.span`
    font-size: 16px;
    position: relative;
    top: 2px;
    margin-right: 12px;
`;

// These CSS rules ensure that every card will be the same height even if the
// insurance banner is displayed.
const CarouselBody = styled.div`
    display: flex;

    ${breakpoint('xsmall', { down: true })} {
        ${InsuranceCheckBanner} + & {
            ${AnimationViewport} {
                margin-top: -${INSURANCE_BANNER_HEIGHT}px;
            }

            ${LocationCardContainer} {
                padding-top: ${INSURANCE_BANNER_HEIGHT + 18}px;
            }
        }
    }
`;

const CarouselFooter = styled.div`
    box-sizing: border-box;
    display: flex;
    height: ${CAROUSEL_FOOTER_HEIGHT}px;
    align-items: center;
    justify-content: center;
    margin-left: 16px;
    margin-top: -${CAROUSEL_FOOTER_HEIGHT}px;
    padding: auto 40px;
    position: relative;
    text-align: center;
    width: calc(100% - 32px);

    ${breakpoint.small} {
        margin-left: 0;
        width: 100%;
    }
`;

const LocationNumberText = styled.span`
    font: 13px/24px ${FontFamily.SharpSansSemibold};
    display: inline-block;
    position: absolute;
    font-size: 10px;
    line-height: 11px;
    color: ${grey75};
    user-select: none;
    left: 16px;

    ${breakpoint.small} {
        left: 37px;
    }
`;

const StyledBreadcrumbDots = styled(BreadcrumbDots)`
    display: flex;
    margin: auto 0;
`;

const Gutter = styled.div`
    align-self: center;
    cursor: ${p => p.children ? 'pointer' : 'default'};

    &:first-child {
        padding-left: 14px;
        text-align: left;
    }

    &:last-child {
        padding-right: 14px;
        text-align: right;
    }

    ${breakpoint('xsmall', { down: true })} {
        display: none;
    }
`;

const StyledArrow = styled(Arrow)`
    font-size: 12.3076923077px !important;
`;
